import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { FormGroup } from '@/components/FormGroup';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { useAuth } from '@/contexts/AuthContext';
import { useUpdateOmnichatCredentials } from '@/hooks/useUpdateOmnichatCredentials';
import i18n from '@/lib/i18n';

const formSchema = z.object({
  apiKey: z.string().min(1, {
    message: i18n.t(
      'pages.Settings.components.Omnichat.Form.validation.apiKeyRequired',
    ),
  }),
  apiSecret: z.string().min(1, {
    message: i18n.t(
      'pages.Settings.components.Omnichat.Form.validation.apiSecretRequired',
    ),
  }),
});

type FormData = z.infer<typeof formSchema>;

export function Form() {
  const { t } = useTranslation('translation', {
    keyPrefix: 'pages.Settings.components.Omnichat.Form',
  });

  const { user } = useAuth();

  const [isEditable, setIsEditable] = useState<boolean>(() => {
    return !user?.customer.omnichatApiKey && !user?.customer.omnichatApiSecret;
  });

  const {
    formState: { errors, isSubmitting },
    register,
    handleSubmit,
    setValue,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
  });

  const { updateOmnichatCredentials } = useUpdateOmnichatCredentials();

  function handleEdit() {
    setValue('apiKey', '');
    setValue('apiSecret', '');
    setIsEditable(true);
  }

  const onSubmit = handleSubmit(async (data) => {
    try {
      await updateOmnichatCredentials({
        omnichatApiKey: data.apiKey,
        omnichatApiSecret: data.apiSecret,
      });

      toast.success(t('toast.success'));
      setIsEditable(false);
    } catch {
      toast.error(t('toast.error'));
    }
  });

  useEffect(() => {
    if (!isEditable) {
      setValue('apiKey', '************************');
      setValue('apiSecret', '************************');
    }
  }, [isEditable, setValue]);

  return (
    <form
      className="flex h-full flex-col space-y-6"
      onSubmit={onSubmit}
      noValidate
    >
      <FormGroup
        className="space-y-2"
        error={{ message: errors.apiKey?.message, font: 'sm' }}
      >
        <Label htmlFor="apiKey">{t('apiKeyLabel')}</Label>

        <Input
          id="apiKey"
          type="password"
          placeholder={t('apiKeyPlaceholder')}
          className="w-full"
          disabled={!isEditable}
          {...register('apiKey')}
        />
      </FormGroup>

      <FormGroup
        className="space-y-2"
        error={{ message: errors.apiSecret?.message, font: 'sm' }}
      >
        <Label htmlFor="apiSecret">{t('apiSecretLabel')}</Label>

        <Input
          id="apiSecret"
          type="password"
          placeholder={t('apiSecretPlaceholder')}
          className="w-full"
          disabled={!isEditable}
          {...register('apiSecret')}
        />
      </FormGroup>

      {isEditable && (
        <Button
          type="submit"
          className="mt-auto w-full bg-black text-white transition-colors hover:bg-gray-800"
          disabled={isSubmitting}
        >
          {isSubmitting ? t('savingButton') : t('saveButton')}
        </Button>
      )}

      {!isEditable && (
        <Button
          type="button"
          className="mt-auto w-full bg-black text-white transition-colors hover:bg-gray-800"
          onClick={handleEdit}
        >
          {t('editButton')}
        </Button>
      )}
    </form>
  );
}
